import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from "react-i18next";
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { loadExpertsForUser, getExpertTier } from '../../lib/expertTiers';
import { getFavoriteMavenIds, toggleMavenFavorite } from '../../lib/favoritesService';
import { useCallAccessControl } from '../../hooks/useCallAccessControl';
import { useCallInitiation } from '../../hooks/useCallInitiation';
import { useModal } from '../../hooks/useModal';
import { compareExpertDirectoryRank } from '../../lib/expertDirectoryRanking';

// Maximum mavens shown to anonymous users + logged-in users whose KYC isn't
// yet approved. Photos are always public; the cap nudges signup/KYC via a
// "see more" CTA without hiding what the platform looks like.
const GATE_LIMIT = 12;
import ConfirmModal from '../ui/ConfirmModal';
import ChatWindow from '../chat/ChatWindow';
import {
  getExpertAvailability,
  getExpertRate,
  getExpertName,
  getExpertExpertise,
  getExpertBio,
  getExpertPhoto,
  getExpertIndustry,
  getExpertTitle,
  parseExpertCallModes,
  useVideoCallAccess,
} from '../../lib/expertUtils';
import OnlineNowBar from './OnlineNowBar';
import SearchFilterBar from './SearchFilterBar';
import PersonCard from './PersonCard';
import FeedSkeleton from './FeedSkeleton';
import ExpandedProfile from './ExpandedProfile';

export default function SocialFeed() {
  const { t } = useTranslation();
  const router = useRouter();
  // Auth + User
  const [userInfo, setUserInfo] = useState(null);
  const [authenticatedUid, setAuthenticatedUid] = useState(null);
  // False until the first onAuthStateChanged callback resolves. Prevents
  // treating a logged-in user as anonymous during the auth-rehydration window
  // (which would gate their profile clicks to /expert/onboarding).
  const [authReady, setAuthReady] = useState(false);
  const [favoriteMavenIds, setFavoriteMavenIds] = useState(new Set());
  const [favoriteLoadingIds, setFavoriteLoadingIds] = useState(new Set());

  // Expert data
  const [experts, setExperts] = useState([]);
  const [brokenPhotoUrls, setBrokenPhotoUrls] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  // Tracks whether the first load has completed — used so the 30s auto-refresh
  // and userInfo-change reloads update data silently instead of greying out
  // every card via the full-page loading state.
  const hasLoadedRef = useRef(false);
  const FEED_CACHE_KEY = 'cmv_feed_experts_v5'; // v5: uploaded-photo-only directory
  // Never persist other users' PII / financial fields to disk in the feed cache.
  const CACHE_SENSITIVE_KEYS = ['email', 'phone', 'phoneNumber', 'contactInfo', 'balance',
    'earnings', 'stripeAccountId', 'stripeConnectStatus', 'payoutSettings', 'bankInfo',
    'ssn', 'dob', 'dateOfBirth', 'address', 'streetAddress', 'kycStatus', 'pushToken',
    'fcmToken', 'expoPushToken'];
  const stripSensitive = (o) => {
    const c = { ...o };
    CACHE_SENSITIVE_KEYS.forEach((k) => delete c[k]);
    return c;
  };

  // A photo request can fail transiently (Safari cache/network issues, an image
  // optimizer timeout, etc.). Older builds persisted that failure forever and
  // consequently hid the Maven from every future search in that browser. Clear
  // the legacy blacklist and keep failures session-only below.
  useEffect(() => {
    try { localStorage.removeItem('cmv_broken_maven_photos_v1'); } catch {}
  }, []);

  useEffect(() => {
    const updateScrollTopVisibility = () => setShowScrollTop(window.scrollY > 700);
    updateScrollTopVisibility();
    window.addEventListener('scroll', updateScrollTopVisibility, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollTopVisibility);
  }, []);

  const handleBrokenMavenPhoto = useCallback((expert, photoURL) => {
    setBrokenPhotoUrls((current) => {
      const next = new Set(current);
      next.add(photoURL);
      return next;
    });
    const id = expert?.uid || expert?.id;
    setExperts((current) => current.filter((item) => (item.uid || item.id) !== id));
  }, []);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(FEED_CACHE_KEY);
      if (cached) {
        const arr = JSON.parse(cached);
        if (Array.isArray(arr) && arr.length) {
          setExperts(arr);
          setLoading(false);
          hasLoadedRef.current = true;
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search + Filters
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    availability: 'all',
    callType: 'all',
    priceRange: 'all',
    expertise: 'all',
    country: 'all',
    city: 'all',
  });

  // Profile modal
  const [selectedExpert, setSelectedExpert] = useState(null);

  // Plan modal
  const { isOpen: isPlanModalOpen, openModal: openPlanModal, closeModal: closePlanModal } = useModal();

  // Access control
  const { canMakeVideoCalls } = useVideoCallAccess(userInfo);
  const { initiateCall, modal, closeModal } = useCallInitiation({ userInfo, setUserInfo, canMakeVideoCalls, openPlanModal });

  useCallAccessControl(userInfo);

  // Signup gate — ONLY anonymous (logged-out) visitors see a limited (12-maven)
  // preview with a "sign up" CTA. KYC does NOT gate browsing for anyone:
  // signed-in seekers and mavens see the full directory + full profiles. KYC is
  // enforced only on money-in (Add Funds / payments) and maven Payout — server-side.
  // Only gate once auth has actually resolved. While auth is still
  // rehydrating (authReady === false) we do NOT gate — otherwise a logged-in
  // user who clicks a maven before their user doc loads gets bounced to the
  // signup/login gate instead of the profile.
  const isAnonymous = authReady && !authenticatedUid;
  const isGated = isAnonymous;

  // Auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthenticatedUid(user?.uid || null);
      try {
        if (user) {
          // Establish signed-in identity immediately. A temporary profile read
          // failure must never make the discovery feed show the signup wall.
          setUserInfo((current) => ({ ...(current || {}), uid: user.uid }));
          try {
            const userSnap = await getDoc(doc(db, 'users', user.uid));
            if (userSnap.exists()) {
              setUserInfo({ uid: user.uid, ...userSnap.data() });
            }
          } catch (profileError) {
            console.warn('Signed-in profile unavailable; continuing without signup gate:', profileError);
            setUserInfo({ uid: user.uid });
          }
        } else {
          setUserInfo(null);
          setFavoriteMavenIds(new Set());
        }
      } finally {
        // Mark auth resolved AFTER userInfo is set, so gating never sees a
        // logged-in user as anonymous mid-load.
        setAuthReady(true);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadFavorites = async () => {
      if (!userInfo?.uid) {
        setFavoriteMavenIds(new Set());
        return;
      }
      const ids = await getFavoriteMavenIds(userInfo.uid);
      setFavoriteMavenIds(ids);
    };

    loadFavorites().catch((error) => {
      console.error('Error loading favorite mavens:', error);
      setFavoriteMavenIds(new Set());
    });
  }, [userInfo?.uid]);

  // Load experts. `isBackground` refreshes (30s auto-refresh) never toggle the
  // full-page loading state; the initial load shows it once.
  const loadExperts = useCallback(async (isBackground = false) => {
    const showSpinner = !isBackground && !hasLoadedRef.current;
    if (showSpinner) setLoading(true);
    try {
      const result = await loadExpertsForUser(userInfo, null);
      const expertsList = Array.isArray(result) ? result : (result?.experts || []);

      // No per-expert fetch: loadExpertsForUser already returns each expert's full
      // doc (incl. contactInfo/location). We only ensure a tier is set. This keeps
      // every refresh to a SINGLE query instead of N+1 reads, so it's fast and cheap.
      const enriched = expertsList.map((expert) => {
        let expertTier = expert.tier;
        if (!expertTier || expertTier === 'undefined') {
          expertTier = getExpertTier({
            title: expert.title || expert.expertProfile?.title || '',
            bio: expert.bio || expert.expertProfile?.bio || '',
            expertise: expert.expertise || expert.expertProfile?.expertise || '',
          });
        }
        // Region is stored at contactInfo.address.{country,city} (see expertDataSync).
        // Hoist to top-level so the country/city filters work AND survive the feed
        // cache (contactInfo is stripped as PII, plain country/city are not).
        const addr = expert.contactInfo?.address || expert.expertProfile?.contactInfo?.address || {};
        const country = (expert.country || addr.country || '').trim();
        const city = (expert.city || addr.city || '').trim();
        return { ...expert, tier: expertTier, country, city };
      });

      setExperts(enriched);
      // Cache for instant paint on the next visit (no initial spinner) — with
      // sensitive fields stripped so no PII/financial data lands in localStorage.
      try { localStorage.setItem(FEED_CACHE_KEY, JSON.stringify(enriched.map(stripSensitive))); } catch {}
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading experts:', error);
      // Fallback
      try {
        const snapshot = await getDocs(collection(db, 'experts'));
        const safeFallback = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((expert) => {
            const photo = getExpertPhoto(expert);
            const hasPhoto = typeof photo === 'string' && /firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(photo);
            const visibleStatus = expert.applicationStatus === 'approved' ||
              (Boolean(authenticatedUid) && expert.role === 'expert' && expert.applicationStatus === 'not_submitted');
            return hasPhoto && visibleStatus;
          });
        setExperts(safeFallback);
      } catch {
        setExperts([]);
      }
      hasLoadedRef.current = true;
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [userInfo, authenticatedUid]);

  // Load experts immediately (public browse) and reload when userInfo changes
  // (plan filtering). After the first load this refreshes silently (no grey-out).
  useEffect(() => {
    loadExperts();
  }, [userInfo, loadExperts]);

  // Auto-refresh every 30s — always a background refresh so cards never flicker.
  useEffect(() => {
    const interval = setInterval(() => loadExperts(true), 30000);
    return () => clearInterval(interval);
  }, [loadExperts]);

  // Filtered + sorted experts
  const filteredExperts = useMemo(() => {
    return experts
      .filter((expert) => {
        // Only surface APPROVED, non-deleted/deactivated experts — for both the
        // public (logged-out) home feed and the authenticated view. Anonymous
        // users can't read the users collection (rules), so we rely on the
        // applicationStatus mirrored onto the experts doc by approve-expert.
        const approved = expert.applicationStatus === 'approved';
        const discoverableNew = Boolean(authenticatedUid) && expert.role === 'expert' && expert.applicationStatus === 'not_submitted';
        const photo = getExpertPhoto(expert);
        const hasPhoto = typeof photo === 'string' && /firebasestorage\.googleapis\.com|storage\.googleapis\.com/i.test(photo);
        const nameLc = (getExpertName(expert) || '').toLowerCase();
        const hidden =
          expert.accountDisabled === true ||
          expert.accountStatus === 'deleted' ||
          expert.accountStatus === 'deactivated' ||
          expert.accountStatus === 'pending_deletion' ||
          expert.applicationStatus === 'deleted' ||
          expert.isActive === false ||
          Boolean(expert.deletionRequestedAt) ||
          expert.isDeleted === true ||
          nameLc.includes('[deleted]');
        if ((!approved && !discoverableNew) || !hasPhoto || brokenPhotoUrls.has(photo) || hidden) return false;

        const searchLower = searchText.toLowerCase().trim();
        const matchesSearch =
          !searchLower ||
          [
            getExpertName(expert),
            getExpertExpertise(expert),
            getExpertBio(expert),
            getExpertIndustry(expert),
            getExpertTitle(expert),
            // Languages spoken — lets seekers search e.g. "Spanish". Stored as an
            // array (custom entries as "Other: <lang>"); flatten to strings.
            ...(Array.isArray(expert.languages) ? expert.languages : []),
            ...(Array.isArray(expert.expertProfile?.languages) ? expert.expertProfile.languages : []),
          ].map((f) => String(f || '').replace(/^Other:\s*/i, '')).some((field) => field.toLowerCase().includes(searchLower));

        const matchesAvailability =
          filters.availability === 'all' || getExpertAvailability(expert) === filters.availability;

        const { supportsAudio, supportsVideo } = parseExpertCallModes(expert);
        const matchesCallType =
          filters.callType === 'all' ||
          (filters.callType === 'audio' && supportsAudio) ||
          (filters.callType === 'video' && supportsVideo);

        const rate = getExpertRate(expert);
        const matchesPrice =
          filters.priceRange === 'all' ||
          (filters.priceRange === 'low' && rate < 2) ||
          (filters.priceRange === 'mid' && rate >= 2 && rate <= 5) ||
          (filters.priceRange === 'high' && rate > 5);

        const matchesExpertise =
          filters.expertise === 'all' || getExpertExpertise(expert) === filters.expertise;

        // Region filters — country/city are stored on the expert doc (from the
        // application form). expertProfile.* is the fallback shape.
        const expCountry = (expert.country || expert.expertProfile?.country || '').trim();
        const expCity = (expert.city || expert.expertProfile?.city || '').trim();
        const matchesCountry = filters.country === 'all' || expCountry === filters.country;
        const matchesCity = filters.city === 'all' || expCity === filters.city;

        return matchesSearch && matchesAvailability && matchesCallType && matchesPrice && matchesExpertise && matchesCountry && matchesCity;
      })
      .sort(compareExpertDirectoryRank);
  }, [experts, searchText, filters, brokenPhotoUrls, authenticatedUid]);

  // Expertise options for filter
  const expertiseOptions = useMemo(() => {
    const set = new Set();
    experts.forEach((expert) => {
      const exp = getExpertExpertise(expert);
      if (exp && exp !== 'General Expert') set.add(exp);
      const t = getExpertTitle(expert);
      if (t && t.trim()) set.add(t);
    });
    return Array.from(set).sort();
  }, [experts]);

  // Country options — only countries that actually have experts.
  const countryOptions = useMemo(() => {
    const set = new Set();
    experts.forEach((expert) => {
      const c = (expert.country || expert.expertProfile?.country || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [experts]);

  // City options — cities that have experts, scoped to the selected country
  // (or all cities when no country is chosen). City resets when country changes.
  const cityOptions = useMemo(() => {
    const set = new Set();
    experts.forEach((expert) => {
      const c = (expert.country || expert.expertProfile?.country || '').trim();
      const city = (expert.city || expert.expertProfile?.city || '').trim();
      if (!city) return;
      if (filters.country === 'all' || c === filters.country) set.add(city);
    });
    return Array.from(set).sort();
  }, [experts, filters.country]);

  // Escape key closes modal
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && selectedExpert) setSelectedExpert(null);
    };
    if (selectedExpert) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedExpert]);

  const onlineCount = experts.filter((e) => getExpertAvailability(e) === 'online').length;
  const onlineCountLabel = onlineCount >= 100 ? '100+' : onlineCount;

  const handleToggleFavorite = useCallback(async (expert) => {
    const mavenId = expert?.uid || expert?.id;

    // Any signed-in user can favorite another maven (mavens included) — only
    // block self-favorite. The seeker-role guard used to make a maven's click
    // a no-op here.
    if (!userInfo?.uid || !mavenId || userInfo.uid === mavenId) return;
    if (favoriteLoadingIds.has(mavenId)) return;

    setFavoriteLoadingIds((prev) => new Set(prev).add(mavenId));
    try {
      const result = await toggleMavenFavorite({
        seekerId: userInfo.uid,
        mavenId,
        seekerName: userInfo.displayName || userInfo.fullName || userInfo.name || userInfo.email || 'Seeker',
        seekerPhotoURL: userInfo.photoURL || '',
      });

      setFavoriteMavenIds((prev) => {
        const next = new Set(prev);
        if (result.isFavorited) {
          next.add(mavenId);
        } else {
          next.delete(mavenId);
        }
        return next;
      });

      setExperts((prev) =>
        prev.map((currentExpert) => {
          const currentId = currentExpert.uid || currentExpert.id;
          if (currentId !== mavenId) return currentExpert;
          return { ...currentExpert, seekerFavoritesCount: result.favoriteCount };
        })
      );

      setSelectedExpert((prev) => {
        if (!prev) return prev;
        const prevId = prev.uid || prev.id;
        if (prevId !== mavenId) return prev;
        return { ...prev, seekerFavoritesCount: result.favoriteCount };
      });
    } catch (error) {
      console.error('Error toggling favorite maven:', error);
      alert(t("discover.favoriteError", "Unable to update favorite right now. Please try again."));
    } finally {
      setFavoriteLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(mavenId);
        return next;
      });
    }
  }, [favoriteLoadingIds, userInfo]);

  // Open the chat in a modal on the Discover page instead of navigating away.
  const [chatModal, setChatModal] = useState(null);

  const handleChat = useCallback(async (expert) => {
    const expertId = expert?.uid || expert?.id;
    if (!expertId || !userInfo?.uid) return;

    try {
      const response = await fetch('/api/chat/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questerId: userInfo.uid, expertId })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start chat');
      // Open in-page modal (full ChatWindow with all controls) rather than /chat/[id].
      setChatModal({ chatId: data.chatId });
    } catch (err) {
      console.error('Error starting chat:', err);
      alert(err.message);
    }
  }, [userInfo?.uid]);

  // Scroll restoration: tapping a maven card saves the feed scroll position,
  // then we restore it after the list re-renders on back-navigation — so the
  // user lands back on the same card instead of the top of the page. (Next's
  // built-in scrollRestoration misses this because the feed loads its list
  // asynchronously, so the page is too short to restore at the moment of back.)
  const restoredScrollRef = useRef(false);
  const goToProfile = useCallback((id) => {
    try { sessionStorage.setItem('cmv_feed_scroll', JSON.stringify({ y: window.scrollY, t: Date.now() })); } catch {}
    router.push(`/expert-profile/${id}`);
  }, [router]);
  useEffect(() => {
    if (restoredScrollRef.current || loading || filteredExperts.length === 0) return;
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem('cmv_feed_scroll') || 'null'); } catch {}
    if (!saved || typeof saved.y !== 'number') return;
    if (Date.now() - (saved.t || 0) > 5 * 60 * 1000) { try { sessionStorage.removeItem('cmv_feed_scroll'); } catch {} return; }
    restoredScrollRef.current = true;
    // Two frames so the grid (and cached cards) have laid out before we scroll.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.scrollTo(0, saved.y);
      try { sessionStorage.removeItem('cmv_feed_scroll'); } catch {}
    }));
  }, [loading, filteredExperts.length]);

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-gray-950">
      {/* Online Now Bar */}
      <OnlineNowBar
        experts={experts}
        onSelectExpert={setSelectedExpert}
        loading={loading}
        gatedCap={0}
        onGatedClick={() => router.push(isAnonymous ? '/expert/onboarding' : '/settings/kyc')}
        onPhotoError={handleBrokenMavenPhoto}
      />

      {/* Search + Filters */}
      <SearchFilterBar
        searchText={searchText}
        setSearchText={setSearchText}
        filters={filters}
        setFilters={setFilters}
        expertiseOptions={expertiseOptions}
        countryOptions={countryOptions}
        cityOptions={cityOptions}
      />

      {/* Results count */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {loading
            ? t("discover.loading", "Loading...")
            : t("discover.peopleAvailable", "{{count}} people available", { count: filteredExperts.length })}
        </p>
        {onlineCount > 0 && !loading && (
          <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
            {t("discover.onlineNow", "{{count}} online now", { count: onlineCountLabel })}
          </p>
        )}
      </div>

      {/* Person Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        {loading ? (
          <FeedSkeleton count={12} />
        ) : filteredExperts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(isGated ? filteredExperts.slice(0, GATE_LIMIT) : filteredExperts).map((expert) => (
                <PersonCard
                  key={expert.id || expert.uid}
                  expert={expert}
                  onCall={initiateCall}
                  onChat={handleChat}
                  onViewProfile={
                    isGated
                      ? () => router.push(isAnonymous ? '/expert/onboarding' : '/settings/kyc')
                      : (expert) => expert.applicationStatus === 'approved'
                        ? goToProfile(expert.uid || expert.id)
                        : setSelectedExpert(expert)
                  }
                  canMakeVideoCalls={canMakeVideoCalls}
                  isFavorited={favoriteMavenIds.has(expert.uid || expert.id)}
                  onToggleFavorite={handleToggleFavorite}
                  favoriteLoading={favoriteLoadingIds.has(expert.uid || expert.id)}
                  canFavorite={
                    !isGated &&
                    !!userInfo?.uid &&
                    userInfo.uid !== (expert.uid || expert.id)
                  }
                  showActions={!isGated}
                  onPhotoError={handleBrokenMavenPhoto}
                />
              ))}
            </div>

            {/* Gate CTA — anonymous users see signup, logged-in no-KYC see KYC */}
            {isGated && filteredExperts.length > GATE_LIMIT && (
              <div className="mt-10 p-8 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-2 border-emerald-200 dark:border-emerald-800 rounded-2xl text-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {isAnonymous
                    ? t("discover.gateMoreMavens", "{{count}}+ more verified Mavens waiting", { count: filteredExperts.length - GATE_LIMIT })
                    : t("discover.gateVerifyTitle", "Verify your identity to unlock the full directory")}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md mx-auto mb-6">
                  {isAnonymous
                    ? t("discover.gateSignupBody", "Sign up free to see everyone, view profiles, and start a call. Takes under a minute.")
                    : t("discover.gateVerifyBody", "Camyvera requires identity verification before you can view full profiles or start calls. Should take under 5 minutes.")}
                </p>
                <button
                  onClick={() => router.push(isAnonymous ? '/expert/onboarding' : '/settings/kyc')}
                  className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-full transition-colors"
                >
                  {isAnonymous ? t("discover.gateSignupCta", "Sign up to see more") : t("discover.gateVerifyCta", "Verify identity")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">
              <span role="img" aria-label={t("discover.searchIconLabel", "search")}>&#128269;</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {t("discover.noResultsTitle", "No results found")}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
              {t("discover.noResultsBody", "Try adjusting your filters or search terms to find more people.")}
            </p>
          </div>
        )}
      </div>

      {/* Expanded Profile Modal */}
      {selectedExpert && (
        <ExpandedProfile
          expert={selectedExpert}
          onCall={initiateCall}
          onClose={() => setSelectedExpert(null)}
          canMakeVideoCalls={canMakeVideoCalls}
          isFavorited={favoriteMavenIds.has(selectedExpert.uid || selectedExpert.id)}
          onToggleFavorite={handleToggleFavorite}
          favoriteLoading={favoriteLoadingIds.has(selectedExpert.uid || selectedExpert.id)}
          currentUserId={userInfo?.uid}
          currentUserRole={userInfo?.role}
          canFavorite={
            !!userInfo?.uid &&
            userInfo.uid !== (selectedExpert.uid || selectedExpert.id)
          }
        />
      )}

      {/* Plan Upgrade Modal */}
      {isPlanModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closePlanModal}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t("discover.upgradeTitle", "Upgrade Your Plan")}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t("discover.upgradeBody", "Unlock more minutes, video calls, and premium features. Add payment method to your account to pay per minute for calls.")}
            </p>
            <button
              onClick={() => (window.location.href = '/settings/payment')}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold mb-3"
            >
              {t("discover.addPaymentMethod", "Add Payment Method")}
            </button>
            <button
              onClick={closePlanModal}
              className="w-full py-3 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold"
            >
              {t("discover.maybeLater", "Maybe Later")}
            </button>
          </div>
        </div>
      )}

      {/* Call Confirmation Modal */}
      <ConfirmModal
        isOpen={modal.isOpen}
        onClose={modal.onClose || closeModal}
        onConfirm={modal.onConfirm || closeModal}
        title={modal.title}
        message={modal.message}
        variant={modal.variant}
      />

      {/* Chat widget — docked bottom-right, chat with a maven without leaving
          Discover. Hosts the full ChatWindow (messages, attachments, send, block…). */}
      {chatModal && userInfo?.uid && (
        <div className="fixed bottom-0 right-0 sm:bottom-4 sm:right-4 z-[60] w-full sm:w-[400px] h-[85vh] sm:h-[600px] sm:max-h-[85vh] bg-white dark:bg-gray-900 sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col border border-gray-200 dark:border-gray-700">
          <ChatWindow
            chatId={chatModal.chatId}
            userId={userInfo.uid}
            userRole={userInfo?.role || 'seeker'}
            onClose={() => setChatModal(null)}
          />
        </div>
      )}

      {showScrollTop && !selectedExpert && !chatModal && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label={t("discover.backToTop", "Back to top")}
          title={t("discover.backToTop", "Back to top")}
          className="fixed bottom-6 right-5 sm:bottom-8 sm:right-8 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 text-2xl font-bold text-white shadow-lg transition hover:bg-emerald-700 hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-emerald-300 dark:focus:ring-emerald-800"
        >
          <span aria-hidden="true">↑</span>
        </button>
      )}
    </div>
  );
}
